import React, { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { selectVersionConfig } from "fond/api";
import { LayoutContext } from "fond/layout/LayoutProvider";
import { selectWidget } from "fond/layout/widgets";
import FeaturesPopup from "fond/map/featureSelection/FeaturesPopup";
import { mergeConfigWithOverlays, useOverlaysWithConfigs } from "fond/map/Overlays/useOverlaysWithConfigs";
import { getCurrentProject } from "fond/project";
import { selectFeature, unselectFeature } from "fond/project/redux";
import { Configuration, Store, Widget } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppDispatch } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

import { SUBAREA_FLOW_SCREENS } from "../constants";

interface IProps {
  layerConfigs: Array<LayerConfig | SublayerConfig>;
}

/**
 * A lightweight component that renders the FeaturesPopup for disambiguating
 * collocated features in the city planner map. This handles both boundary/subarea
 * features and overlay features.
 *
 * Unlike the main map's FeatureHandler, this component does not handle comments
 * or URL-based feature selection.
 */
const OverlayFeaturePopup: React.FC<IProps> = ({ layerConfigs }: IProps) => {
  const layout = useContext(LayoutContext);
  const dispatch = useAppDispatch();
  const { stack } = useStackedNavigationContext();
  const featureSelectionPopup = useSelector((state: Store) => state.project.featureSelectionPopup);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const config = useSelector((state: Store) => selectVersionConfig(state, versionId));

  const { overlays: overlayConfig, overlayLayerConfigs } = useOverlaysWithConfigs(versionId, project);

  const mergedConfig = useMemo((): Configuration => mergeConfigWithOverlays(config, overlayConfig), [config, overlayConfig]);

  const mergedLayerConfigs = useMemo(
    (): Array<LayerConfig | SublayerConfig> => [...layerConfigs, ...overlayLayerConfigs],
    [layerConfigs, overlayLayerConfigs]
  );

  const onFeatureSelected = useCallback(
    (feature: mapboxgl.MapboxGeoJSONFeature) => {
      const sourceVersionId = (feature.layer as { metadata?: { versionId?: string } })?.metadata?.versionId;
      dispatch(selectFeature(feature, featureSelectionPopup.lngLat, true, sourceVersionId));

      // Don't auto-select properties widget when in subarea selection mode
      const isSubareaFlow = SUBAREA_FLOW_SCREENS.includes(stack.at(-1) ?? "");
      if (layout?.model && !isSubareaFlow) {
        selectWidget(layout.model, Widget.CityProperties);
      }
    },
    [dispatch, featureSelectionPopup, layout, stack]
  );

  const onClose = useCallback(() => {
    dispatch(unselectFeature());
  }, [dispatch]);

  if (!featureSelectionPopup) return null;

  return (
    <FeaturesPopup
      features={featureSelectionPopup.features}
      lngLat={featureSelectionPopup.lngLat}
      layerConfigs={mergedLayerConfigs}
      config={mergedConfig}
      onClose={onClose}
      onFeatureClick={onFeatureSelected}
    />
  );
};

export default OverlayFeaturePopup;
